import { AddOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { Category, Locale, Unit } from '@synergy/models';
import { Filter, PoolSpinner, SearchFilter, ToggleFilter } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom';
import { PoolDocumentTitle } from '@synergy/components-next';
import { useEffect, useState } from 'react';

import { Attribute } from '../../../../common/models/attribute';
import { AttributesDetailModal } from '../attributes/attributes-detail-modal';
import { AttributesOverview } from '../attributes/attributes-overview';
import { CategoriesDetailModal } from '../categories/categories-detail-modal';
import { CategoriesOverviewTable } from '../categories/categories-overview-table';
import { ConfigurationTab } from '../models/configuration-tab';
import { ConfigurationTag } from '../tags/models/configuration-tag';
import { PaloActionMenu } from '../../../../common/components/palo/palo-action-menu';
import { PaloConfigurationShell } from '../../palo/palo-configuration-shell';
import { PaloLanguageSwitch } from '../../../../common/components/palo/palo-language-switch';
import { TagsDetailModal } from '../tags/tags-detail-modal';
import { TagsOverview } from '../tags/tags-overview';
import { UnitsDetailModal } from '../units/units-detail-modal';
import { UnitsOverview } from '../units/units-overview';
import { changeSelectedLanguage, resetSelectedLanguage } from '../../../../common/actions/selected-language-actions';
import { defaultSearchParamsSelector } from '../../../../store/default-searchparams-selector';
import { fetchEclasses } from '../../../../common/actions/eclass-actions';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface CategoryEditItem {
    category: Category;
    depth: number;
    unavailableEclasses: number[];
}

export const PaloConfigurationDataOverview = () => {
    const dispatch = useThunkDispatch();
    const { '*': selectedTab } = useParams();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const { supportedLocales } = useAppSelector(state => state);

    const { requesting: attributesRequesting } = useAppSelector(state => state.attributes);
    const { requesting: unitsRequesting } = useAppSelector(state => state.units);
    const { requesting: categoriesRequesting } = useAppSelector(state => state.categories);
    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams);

    const [isAttributeModalOpen, setIsAttributeModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [selectedAttribute, setSelectedAttribute] = useState<Attribute | undefined>(undefined);
    const [selectedCategory, setSelectedCategory] = useState<CategoryEditItem | undefined>(undefined);
    const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>(undefined);
    const [selectedTag, setSelectedTag] = useState<ConfigurationTag | undefined>(undefined);
    const [toggleActive, setToggleActive] = useState(true);

    const getTabSearchParams = (tab: ConfigurationTab) => {
        const params = new URLSearchParams(searchParams);
        params.delete('includeImported');
        if (tab === ConfigurationTab.Attributes && !toggleActive) {
            params.set('includeImported', 'true');
        }
        return params;
    };

    useEffect(() => {
        dispatch(fetchEclasses());
        dispatch(resetSelectedLanguage());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCategoryEdit = (category: Category, depth: number, unavailableEclasses: number[]) => {
        setSelectedCategory({ category, depth, unavailableEclasses });
        setIsCategoryModalOpen(true);
    };

    const handleAttributeEdit = (attribute: Attribute) => {
        setSelectedAttribute(attribute);
        setIsAttributeModalOpen(true);
    };

    const handleUnitEdit = (unit: Unit) => {
        setSelectedUnit(unit);
        setIsUnitModalOpen(true);
    };

    const handleTagEdit = (tag: ConfigurationTag) => {
        setSelectedTag(tag);
        setIsTagModalOpen(true);
    };

    const handleNewAttribute = () => {
        setSelectedAttribute(undefined);
        setIsAttributeModalOpen(true);
    };

    const handleAttributesModalClose = () => {
        setSelectedAttribute(undefined);
        setIsAttributeModalOpen(false);
    };

    const handleNewCategory = () => {
        setSelectedAttribute(undefined);
        setIsCategoryModalOpen(true);
    };

    const handleCategoryModalClose = () => {
        setSelectedCategory(undefined);
        setIsCategoryModalOpen(false);
    };

    const handleNewUnit = () => {
        setSelectedUnit(undefined);
        setIsUnitModalOpen(true);
    };

    const handleUnitModalClose = () => {
        setSelectedUnit(undefined);
        setIsUnitModalOpen(false);
    };

    const handleNewTag = () => {
        setSelectedTag(undefined);
        setIsTagModalOpen(true);
    };

    const handleTagModalClose = () => {
        setSelectedTag(undefined);
        setIsTagModalOpen(false);
    };

    const handleLanguageSwitch = (locale: Locale) => {
        dispatch(changeSelectedLanguage(locale));
    };

    return (
        <PoolSpinner
            enabled={attributesRequesting || categoriesRequesting || unitsRequesting}
            sx={{ display: 'block', height: '100%' }}
        >
            <Filter searchParams={searchParams} onUpdateSearchParams={setSearchParams}>
                <PaloConfigurationShell
                    title="app.configuration.title.data"
                    tabs={[
                        {
                            value: ConfigurationTab.Categories,
                            label: 'app.configuration.categories',
                            searchParams: getTabSearchParams(ConfigurationTab.Categories).toString(),
                        },
                        {
                            value: ConfigurationTab.Units,
                            label: 'app.configuration.units',
                            searchParams: getTabSearchParams(ConfigurationTab.Units).toString(),
                        },
                        {
                            value: ConfigurationTab.Attributes,
                            label: 'app.configuration.attributes',
                            searchParams: getTabSearchParams(ConfigurationTab.Attributes).toString(),
                        },
                        {
                            value: ConfigurationTab.Tags,
                            label: 'app.configuration.tags',
                            searchParams: getTabSearchParams(ConfigurationTab.Tags).toString(),
                        },
                    ]}
                    actions={
                        <>
                            {selectedTab === ConfigurationTab.Attributes && (
                                <ToggleFilter
                                    filterName="includeImported"
                                    inverted={true}
                                    value={toggleActive}
                                    onChange={setToggleActive}
                                />
                            )}
                            <Box sx={{ width: 220 }}>
                                <SearchFilter />
                            </Box>
                            {selectedTab !== ConfigurationTab.Tags && <PaloLanguageSwitch />}
                            <PaloActionMenu
                                primary={true}
                                label={<FormattedMessage id="app.dashboard.new" />}
                                icon={<AddOutlined />}
                                items={[
                                    {
                                        label: <FormattedMessage id="app.configuration.newCategory" />,
                                        onClick: handleNewCategory,
                                    },
                                    {
                                        label: <FormattedMessage id="app.configuration.newUnit" />,
                                        onClick: handleNewUnit,
                                    },
                                    {
                                        label: <FormattedMessage id="app.configuration.newAttribute" />,
                                        onClick: handleNewAttribute,
                                    },
                                    {
                                        label: <FormattedMessage id="app.configuration.newTag" />,
                                        onClick: handleNewTag,
                                    },
                                ]}
                            />
                        </>
                    }
                >
                    <Routes>
                        <Route
                            index={true}
                            element={
                                <Navigate
                                    to={{ pathname: ConfigurationTab.Categories, search: searchParams.toString() }}
                                    replace={true}
                                />
                            }
                        />
                        <Route
                            path={ConfigurationTab.Categories}
                            element={<CategoriesOverviewTable onEdit={handleCategoryEdit} />}
                        />
                        <Route path={ConfigurationTab.Units} element={<UnitsOverview onEdit={handleUnitEdit} />} />
                        <Route
                            path={ConfigurationTab.Attributes}
                            element={<AttributesOverview onEdit={handleAttributeEdit} />}
                        />
                        <Route path={ConfigurationTab.Tags} element={<TagsOverview onEdit={handleTagEdit} />} />
                    </Routes>
                </PaloConfigurationShell>
            </Filter>

            <AttributesDetailModal
                visible={isAttributeModalOpen}
                onClose={handleAttributesModalClose}
                attribute={selectedAttribute}
            />
            <CategoriesDetailModal
                visible={isCategoryModalOpen}
                onClose={handleCategoryModalClose}
                category={selectedCategory?.category}
                unavailableEclasses={selectedCategory?.unavailableEclasses}
                depth={selectedCategory?.depth ?? 0}
            />
            <UnitsDetailModal visible={isUnitModalOpen} onClose={handleUnitModalClose} unit={selectedUnit} />
            <TagsDetailModal visible={isTagModalOpen} onClose={handleTagModalClose} tag={selectedTag} />
        </PoolSpinner>
    );
};
